import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { db } from "@/lib/firebase";
import { addDoc, collection, Timestamp } from "firebase/firestore";
import { getNextDocumentNumber } from "@/lib/documentNumber";
import { printDocument } from "@/lib/printHelper";
import ServiceBillForm from "@/components/forms/ServiceBillForm";
import ServiceBillPreview from "@/components/preview/ServiceBillPreview";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Printer, Save, RotateCcw, Eye, Edit, Loader2 } from "lucide-react";

const getInitialFormData = (billNumber = "") => ({
  billNumber,
  billDate: new Date().toISOString().split("T")[0],
  customerName: "",
  customerAddress: "",
  customerPhone: "",
  vehicleModel: "",
  vehicleNumber: "",
  chassisNumber: "",
  kmReading: "",
  serviceType: "",
  complaints: "",
  serviceItems: [{ description: "", quantity: "1", rate: "", amount: "" }],
  partsItems: [{ partName: "", partNumber: "", quantity: "1", rate: "", amount: "" }],
  laborCharges: "",
  cgst: "",
  sgst: "",
  discount: "",
  paymentMode: "",
  technicianName: "",
  notes: "",
});

const ServiceBillPage = () => {
  const [formData, setFormData] = useState(getInitialFormData("Loading..."));
  const [activeTab, setActiveTab] = useState("form");
  const [isSaving, setIsSaving] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [numberLoading, setNumberLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    const loadNumber = async () => {
      setNumberLoading(true);
      const num = await getNextDocumentNumber("SER", "service_bills", "billNumber");
      setFormData(getInitialFormData(num));
      setNumberLoading(false);
    };
    loadNumber();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceItemChange = (index, field, value) => {
    const items = [...formData.serviceItems];
    items[index][field] = value;
    if (field === "quantity" || field === "rate") {
      items[index].amount = String((parseFloat(items[index].quantity) || 0) * (parseFloat(items[index].rate) || 0));
    }
    setFormData((prev) => ({ ...prev, serviceItems: items }));
  };

  const handlePartsItemChange = (index, field, value) => {
    const items = [...formData.partsItems];
    items[index][field] = value;
    if (field === "quantity" || field === "rate") {
      items[index].amount = String((parseFloat(items[index].quantity) || 0) * (parseFloat(items[index].rate) || 0));
    }
    setFormData((prev) => ({ ...prev, partsItems: items }));
  };

  const addServiceItem = () => setFormData((prev) => ({ ...prev, serviceItems: [...prev.serviceItems, { description: "", quantity: "1", rate: "", amount: "" }] }));
  const removeServiceItem = (i) => { if (formData.serviceItems.length > 1) setFormData((prev) => ({ ...prev, serviceItems: prev.serviceItems.filter((_, idx) => idx !== i) })); };
  const addPartsItem = () => setFormData((prev) => ({ ...prev, partsItems: [...prev.partsItems, { partName: "", partNumber: "", quantity: "1", rate: "", amount: "" }] }));
  const removePartsItem = (i) => { if (formData.partsItems.length > 1) setFormData((prev) => ({ ...prev, partsItems: prev.partsItems.filter((_, idx) => idx !== i) })); };

  const handleReset = async () => {
    setNumberLoading(true);
    const num = await getNextDocumentNumber("SER", "service_bills", "billNumber");
    setFormData(getInitialFormData(num));
    setNumberLoading(false);
    setActiveTab("form");
    toast.success("Form reset successfully");
  };

  const calcServiceTotal = () => formData.serviceItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const calcPartsTotal = () => formData.partsItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const calcTax = () => (parseFloat(formData.cgst) || 0) + (parseFloat(formData.sgst) || 0);
  const calculateTotal = () => calcServiceTotal() + calcPartsTotal() + (parseFloat(formData.laborCharges) || 0) + calcTax() - (parseFloat(formData.discount) || 0);

  // Required fields validation
  const validate = () => {
    const required = [
      { key: "customerName", label: "Customer Name" },
      { key: "customerPhone", label: "Phone Number" },
      { key: "vehicleModel", label: "Vehicle Model" },
      { key: "serviceType", label: "Service Type" },
    ];
    for (const f of required) {
      if (!formData[f.key] || formData[f.key].toString().trim() === "") {
        toast.error(`Please fill in: ${f.label}`);
        setActiveTab("form");
        return false;
      }
    }
    // Validate at least one service item has a description
    const hasServiceWork = formData.serviceItems.some(
      (item) => item.description && item.description.trim() !== ""
    );
    if (!hasServiceWork) {
      toast.error("Please add at least one Service Work item description");
      setActiveTab("form");
      return false;
    }
    return true;
  };

  const saveToDb = async () => {
    const billRef = await addDoc(collection(db, "service_bills"), {
      ...formData,
      total: calculateTotal(),
      savedAt: Timestamp.now(),
    });



    return billRef;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      await saveToDb();
      toast.success(`Service bill ${formData.billNumber} saved successfully!`);
      const nextNum = await getNextDocumentNumber("SER", "service_bills", "billNumber");
      setFormData(getInitialFormData(nextNum));
      setActiveTab("form");
    } catch (error) {
      toast.error("Failed to save service bill. Check Firebase configuration.");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrint = async () => {
    if (!validate()) return;

    // 1. Switch to preview FIRST so the document renders before print fires
    setActiveTab("preview");
    setIsPrinting(true);

    // 2. Wait for React to paint the preview
    await new Promise((resolve) => setTimeout(resolve, 500));

    // 3. Open print dialog via printHelper (clones to body level for reliable A4)
    await printDocument();

    // 4. After dialog closes: save, toast, reset
    try {
      await saveToDb();
      toast.success(`Service bill ${formData.billNumber} saved & printed!`);
      const nextNum = await getNextDocumentNumber("SER", "service_bills", "billNumber");
      setFormData(getInitialFormData(nextNum));
      setActiveTab("form");
    } catch (error) {
      toast.error("Printed but failed to save. Check Firebase.");
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex flex-wrap items-center justify-between gap-4 no-print">
        <div>
          <h2 className="font-heading text-xl font-semibold text-foreground">Service Bill</h2>
          <p className="text-sm text-muted-foreground">Create and print service/repair bill</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleReset} className="gap-2">
            <RotateCcw className="h-4 w-4" /> Reset
          </Button>
          <Button variant="secondary" onClick={handleSave} className="gap-2" disabled={isSaving || numberLoading}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isSaving ? "Saving..." : "Save"}
          </Button>
          <Button onClick={handlePrint} className="gap-2 bg-primary text-primary-foreground" disabled={isPrinting || numberLoading}>
            {isPrinting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
            {isPrinting ? "Saving & Printing..." : "Print"}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2 no-print">
          <TabsTrigger value="form" className="gap-2"><Edit className="h-4 w-4" /> Edit Form</TabsTrigger>
          <TabsTrigger value="preview" className="gap-2"><Eye className="h-4 w-4" /> Preview</TabsTrigger>
        </TabsList>

        <TabsContent value="form" className="mt-4">
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading">Service Details</CardTitle></CardHeader>
            <CardContent>
              <ServiceBillForm
                formData={formData}
                onInputChange={handleInputChange}
                onSelectChange={handleSelectChange}
                onServiceItemChange={handleServiceItemChange}
                onPartsItemChange={handlePartsItemChange}
                addServiceItem={addServiceItem}
                removeServiceItem={removeServiceItem}
                addPartsItem={addPartsItem}
                removePartsItem={removePartsItem}
                calculateTotal={calculateTotal}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preview" className="mt-4">
          <div className="no-print mb-3 p-3 bg-secondary/30 rounded-lg border border-border text-sm text-muted-foreground">
            Click <strong>Print</strong> to send to printer. The preview below shows the exact A4 output.
          </div>
          <ServiceBillPreview
            ref={printRef}
            formData={formData}
            serviceTotal={calcServiceTotal()}
            partsTotal={calcPartsTotal()}
            tax={calcTax()}
            total={calculateTotal()}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ServiceBillPage;
